import { normalizePhoneCountryBR } from "@/utils/phone";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { buildLinhasFormatadas, getOperadoraWithFallback } from './TriagemStandardColumns';
import { equipesDoLider, filtrarPeloLider } from '../triagem/triagemPedidoUtils';
import { useUserProfileContext } from '@/contexts/UserProfileContext';
import { usePedidoMovement } from '@/hooks/usePedidoMovement';
import { depoisDoClique, manterNaTriagem, meuUsuarioId } from '@/components/kanban/triagem/acaoTriagem';

const QUADRO_PRE_ATIVO: string = 'PRE_ATIVO';
const COLUNA = 'CPF / CNPJ DIVERGENTE';
const CHAVE = 'triagem-cpf-divergente';

export interface ChamadoCpfDivergente {
  id: string;
  numero_chamado: string;
  status: string;
  observacao: string | null;
  created_at: string;
}

export interface ItemTriagemCpfDivergente {
  item_id: string;
  pedido_id: string;
  numero_pedido: string;
  item_seq: number | null;
  cnpj: string;
  /** CNPJ ou CPF — a fila mostra um segmento por vez (`segmentoTriagem.ts`). */
  tipo_pessoa: string | null;
  /** CNPJ ou CPF, como está no banco. Máscara em `formatarDocumento`. */
  documento: string;
  razao_social: string;
  linha: string;
  linhas_formatadas: string[];
  operadora_doadora: string;
  numero_cotacao_operadora: string;
  plano_nome: string;
  tipo_chip: string;
  faturamento_tipo: string;
  agenda_tipo: string;
  data_agendamento: string;
  contato_real_nome: string;
  contato_real_telefone: string;
  cessionario_nome: string;
  cessionario_cpf: string;
  entrada_coluna_em: string;
  status_id: string;
  coluna_atual: string;
  quadro_atual: string;
  cliente_id: string;
  is_first_of_cnpj: boolean;
  chamados: ChamadoCpfDivergente[];
  has_doc_cedente: boolean;
  doc_cedente_path: string | null;
}

export function useTriagemCpfDivergente() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useUserProfileContext();
  const { refetchInterval, onQueryError, onQuerySuccess } = useSmartPolling({
    weight: 'B',
    baseInterval: POLLING_INTERVALS.PESO_B,
  });

  const { data: itens = [], isLoading, refetch } = useQuery({
    queryKey: ['triagem-cpf-divergente'],
    queryFn: async () => {
      const lider = await equipesDoLider(profile);
      const { data, error } = await filtrarPeloLider(supabase
        .from('v_planilha_mae')
        .select('item_id, pedido_id, numero_pedido, item_seq, cnpj, razao_social, linha, contato_real_nome, contato_real_telefone, cessionario_nome, cessionario_cpf, entrada_coluna_em, status_id, coluna_atual, quadro_atual, cliente_id, manter_em, linhas_telefone, numero_cotacao_operadora, plano_nome, tipo_chip, faturamento_tipo, agenda_tipo, data_agendamento, ddd, operadora_doadora, tipo_pessoa, documento')
        .eq('coluna_atual', 'CPF / CNPJ DIVERGENTE')
        .eq('quadro_atual', 'PRE_ATIVO')
        .order('manter_em', { ascending: true, nullsFirst: true })
        .order('cnpj', { ascending: true })
        .order('pedido_id', { ascending: true })
        .order('entrada_coluna_em', { ascending: true }), profile, lider);

      if (error) { onQueryError(error); throw error; }
      onQuerySuccess();

      const items = (data || []) as any[];
      if (items.length === 0) return [];

      // Buscar chamados
      const itemIds = items.map(i => i.item_id);
      const { data: chamados } = await supabase
        .from('chamados_cpf_divergente')
        .select('id, item_id, numero_chamado, status, observacao, created_at')
        .in('item_id', itemIds)
        .order('created_at', { ascending: true });

      const chamadosByItem: Record<string, ChamadoCpfDivergente[]> = {};
      (chamados || []).forEach((c: any) => {
        if (!chamadosByItem[c.item_id]) chamadosByItem[c.item_id] = [];
        chamadosByItem[c.item_id].push({
          id: c.id,
          numero_chamado: c.numero_chamado,
          status: c.status,
          observacao: c.observacao,
          created_at: c.created_at,
        });
      });

      // Buscar anexos DOC_CESSIONARIO
      const pedidoIds = [...new Set(items.map(i => i.pedido_id))];
      const { data: anexos } = await supabase
        .from('anexos_crm')
        .select('pedido_id, path')
        .in('pedido_id', pedidoIds)
        .eq('tipo_anexo', 'DOC_CESSIONARIO');

      const docByPedido: Record<string, string> = {};
      (anexos || []).forEach((a: any) => {
        docByPedido[a.pedido_id] = a.path;
      });

      let lastCnpj = '';
      const processed: ItemTriagemCpfDivergente[] = items.map((item: any) => {
        const isFirst = item.cnpj !== lastCnpj;
        lastCnpj = item.cnpj || '';
        const linhasF = buildLinhasFormatadas(item);
        return {
          item_id: item.item_id,
          pedido_id: item.pedido_id,
          numero_pedido: String(item.numero_pedido || ''),
          item_seq: item.item_seq ?? null,
          cnpj: item.cnpj || '',
          tipo_pessoa: item.tipo_pessoa ?? null,
          documento: item.documento || item.cnpj || '',
          razao_social: item.razao_social || '',
          linha: linhasF.length > 0 ? linhasF.join(', ') : (item.linha || '-'),
          linhas_formatadas: linhasF,
          operadora_doadora: getOperadoraWithFallback(item),
          numero_cotacao_operadora: item.numero_cotacao_operadora || '',
          plano_nome: item.plano_nome || '',
          tipo_chip: item.tipo_chip || '',
          faturamento_tipo: item.faturamento_tipo || '',
          agenda_tipo: item.agenda_tipo || '',
          data_agendamento: item.data_agendamento || '',
          contato_real_nome: item.contato_real_nome || '',
          contato_real_telefone: item.contato_real_telefone || '',
          cessionario_nome: item.cessionario_nome || '',
          cessionario_cpf: item.cessionario_cpf || '',
          entrada_coluna_em: item.entrada_coluna_em || '',
          status_id: item.status_id || '',
          coluna_atual: item.coluna_atual || 'CPF / CNPJ DIVERGENTE',
          quadro_atual: item.quadro_atual || 'PRE_ATIVO',
          cliente_id: item.cliente_id || '',
          is_first_of_cnpj: isFirst,
          chamados: chamadosByItem[item.item_id] || [],
          has_doc_cedente: !!docByPedido[item.pedido_id],
          doc_cedente_path: docByPedido[item.pedido_id] || null,
        };
      });
      return processed;
    },
    refetchInterval,
  });

  const { moverPedidoJunto } = usePedidoMovement();

  const registrarMovimento = async (
    pedidoId: string, tipo: string, motivo: string,
    icone: string, cor: string, cnpj?: string
  ) => {
    await supabase.from('movimento').insert({
      entidade: 'pedido',
      entidade_id: pedidoId,
      tipo,
      motivo,
      icone,
      cor,
      usuario_id: await meuUsuarioId(),
      cnpj: cnpj || null,
    } as never);
  };

  // Registrar chamado IW
  const registrarChamado = useMutation({
    mutationFn: async ({ itemId, pedidoId, numeroChamado, cnpj }: { itemId: string; pedidoId: string; numeroChamado: string; cnpj?: string }) => {
      const [usuarioId, { count }] = await Promise.all([
        meuUsuarioId(),
        supabase
          .from('chamados_cpf_divergente')
          .select('id', { count: 'exact', head: true })
          .eq('item_id', itemId),
      ]);

      const numChamado = (count || 0) + 1;

      const { error } = await supabase
        .from('chamados_cpf_divergente')
        .insert({
          item_id: itemId,
          pedido_id: pedidoId,
          numero_chamado: numeroChamado,
          usuario_id: usuarioId,
          status: 'ABERTO',
        } as never);

      if (error) throw new Error(error.message);

      await registrarMovimento(
        pedidoId, 'chamado_cpf',
        `Chamado CPF Divergente #${numChamado} registrado: ${numeroChamado}`,
        'file-text', 'amber', cnpj
      );

      return { itemId, pedidoId };
    },
    onSuccess: ({ pedidoId }) => {
      toast({ title: 'Chamado registrado', description: 'Número do chamado IW salvo com sucesso.' });
      void queryClient.invalidateQueries({ queryKey: [CHAVE] });
      void queryClient.invalidateQueries({ queryKey: ['timeline-pedido', pedidoId] });
      void queryClient.invalidateQueries({ queryKey: ['timeline-pedido-modal'] });
    },
    onError: (error) => {
      toast({ title: 'Erro ao registrar chamado', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    },
  });

  // O pedido de titularidade ao cliente saiu daqui (Douglas 24/09/2026): mandava
  // para um n8n desligado e gravava "WhatsApp enviado" na timeline sem ter
  // enviado. Agora é `usePedirTitularidade`, pela conversa do pedido.

  // Resolver com nova cotação → INSPEÇÃO CONCLUÍDA. A cotação nova entra na
  // linha que ainda está nesta fila e a linha anda numa ida
  // (`fn_mover_pedido_junto`, que grava o histórico); os chamados e o registro
  // na timeline saem juntos depois.
  const resolverComNovaCotacao = useMutation({
    mutationFn: async ({ itemId, novaCotacao, cnpj }: { itemId: string; novaCotacao: string; cnpj?: string }) => {
      const { data: salvos, error: updateError } = await supabase
        .from('itens_pedido')
        .update({ numero_cotacao_operadora: novaCotacao } as never)
        .eq('id', itemId)
        .eq('coluna_atual', COLUNA)
        .select('id, pedido_id');

      if (updateError) throw new Error(updateError.message);
      const item = (salvos as { id: string; pedido_id: string }[] | null)?.[0];
      if (!item) throw new Error('Item não encontrado ou já foi processado');

      const motivo = `CPF Divergente resolvido - Nova Cotação IW: ${novaCotacao}`;
      await moverPedidoJunto({
        pedidoId: item.pedido_id,
        itensIds: [itemId],
        quadroOrigem: QUADRO_PRE_ATIVO,
        colunaOrigem: COLUNA,
        toQuadro: QUADRO_PRE_ATIVO as Parameters<typeof moverPedidoJunto>[0]['toQuadro'],
        toColuna: 'INSPEÇÃO CONCLUIDA',
        motivo,
        origemTriagem: true,
      });

      await Promise.all([
        supabase
          .from('chamados_cpf_divergente')
          .update({ status: 'RESOLVIDO' } as never)
          .eq('item_id', itemId)
          .eq('status', 'ABERTO'),
        registrarMovimento(
          item.pedido_id, 'status',
          `${motivo}. Movido para Inspeção Concluída.`,
          'check-circle', 'green', cnpj
        ),
      ]);

      return { itemId, pedidoId: item.pedido_id };
    },
    onSuccess: ({ itemId, pedidoId }) => {
      toast({ title: 'Resolvido', description: 'Nova cotação salva e item movido para Inspeção Concluída.' });
      void depoisDoClique(queryClient, [CHAVE, 'triagem-ativacao'], { pedidoId, itens: [itemId] }, 'sai');
    },
    onError: (error) => {
      toast({ title: 'Erro ao resolver', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    },
  });

  // Manter no final da fila: o registro na timeline e o manter_em numa ida
  // (`fn_triagem_manter`); só desce a linha que ainda está nesta fila.
  const moverParaFinal = useMutation({
    mutationFn: async ({ itemId, pedidoId }: { itemId: string; pedidoId: string; cnpj?: string }) => {
      await manterNaTriagem({
        pedidoId,
        itens: [itemId],
        coluna: COLUNA,
        movimento: 'Mantido na fila de CPF/CNPJ Divergente',
      });
      return { itemId, pedidoId };
    },
    onSuccess: ({ itemId, pedidoId }) => {
      toast({ title: 'Manter', description: 'Item movido para o final da fila.' });
      void depoisDoClique(queryClient, [CHAVE], { pedidoId, itens: [itemId] }, 'desce');
    },
    onError: (error) => {
      toast({ title: 'Erro', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    },
  });

  // Download Doc. Cedente
  const downloadDocCedente = async (path: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('anexos-crm')
        .download(path);

      if (error) throw new Error(error.message);

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = path.split('/').pop() || 'doc-cedente';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: 'Erro ao baixar', description: 'Não foi possível baixar o Doc. Cedente.', variant: 'destructive' });
    }
  };

  return {
    itens,
    isLoading,
    refetch,
    registrarChamado: registrarChamado.mutateAsync,
    isRegistrandoChamado: registrarChamado.isPending,
    resolverComNovaCotacao: resolverComNovaCotacao.mutateAsync,
    isResolvendo: resolverComNovaCotacao.isPending,
    moverParaFinal: moverParaFinal.mutateAsync,
    isMovingToFinal: moverParaFinal.isPending,
    downloadDocCedente,
  };
}
